import axios from "axios";
import { API_URL } from "./apiConfig";
import { readAccessToken } from "./authTokenUtils";

let isHandlingUnauthorized = false;
let isRefreshingToken = false;
let refreshWaiters = [];

function notifyRefreshWaiters(newToken) {
  const waiters = refreshWaiters;
  refreshWaiters = [];
  waiters.forEach((fn) => {
    try {
      fn(newToken);
    } catch {
      // ignore
    }
  });
}

function looksLikeNoSubscription(detail) {
  const d = String(detail || "").toLowerCase();
  return (
    d.includes("no active subscription") ||
    d.includes("no subscription") ||
    d.includes("subscription not found") ||
    d.includes("not found")
  );
}

function isPublicRoute(pathname) {
  const p = (pathname || "").toLowerCase();
  return (
    p === "/login" ||
    p === "/register" ||
    p === "/auth/callback" ||
    p === "/confirm-email" ||
    p === "/reset-password" ||
    p === "/legal" ||
    p.startsWith("/catalog/")
  );
}

function isAdminAppRoute(pathname) {
  const p = (pathname || "").toLowerCase();
  return p === "/admin" || p.startsWith("/admin/");
}

function isAdminApiRequest(url) {
  const u = String(url || "").toLowerCase();
  return u.includes("/admin/login") || u.includes("/admin/");
}

const ADMIN_TOKEN_KEY = "kataloji_admin_token";

/** FastAPI + Header(...) used to return 422 when Authorization was absent; treat like 401. */
function isMissingAuthorization422(error) {
  if (error?.response?.status !== 422) return false;
  const detail = error?.response?.data?.detail;
  if (!Array.isArray(detail)) return false;
  return detail.some(
    (item) =>
      Array.isArray(item?.loc) &&
      item.loc[0] === "header" &&
      String(item.loc[1] || "").toLowerCase() === "authorization"
  );
}

/**
 * Install a global Axios response interceptor.
 * Any 401 response will clear auth and redirect to /login.
 */
export function installAxiosUnauthorizedRedirect() {
  // Avoid installing the interceptor multiple times (StrictMode renders twice in dev).
  if (axios.__katalojiUnauthorizedInterceptorInstalled) return;
  axios.__katalojiUnauthorizedInterceptorInstalled = true;

  // Attach token automatically when available (works with Axios 1.x AxiosHeaders).
  axios.interceptors.request.use((config) => {
    try {
      const token = readAccessToken();
      if (!token) return config;
      const headers = config.headers || {};
      const existing =
        (typeof headers.get === "function" &&
          (headers.get("Authorization") || headers.get("authorization"))) ||
        headers.Authorization ||
        headers.authorization;
      if (!existing) {
        const bearer = `Bearer ${token}`;
        if (typeof headers.set === "function") headers.set("Authorization", bearer);
        else {
          config.headers = headers;
          config.headers.Authorization = bearer;
        }
      }
    } catch {
      // ignore
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      let status = error?.response?.status;
      const url = String(error?.config?.url || "");
      const originalRequest = error?.config;

      // Legacy servers: missing Authorization → 422; handle like 401 for redirect/refresh.
      if (isMissingAuthorization422(error)) {
        status = 401;
      }

      // Some legacy setups return 401 for "no subscription" on this endpoint.
      // Be safe: only skip logout for known "no subscription" messages.
      if (status === 401 && url.includes("/subscription/my-subscription")) {
        const detail =
          error?.response?.data?.detail ??
          error?.response?.data?.message ??
          "";
        if (looksLikeNoSubscription(detail)) return Promise.reject(error);
        // Otherwise: treat as a real auth error and continue to the normal 401 handler.
      }

      // Platform admin API (wrong password, expired admin token) — never merchant /login redirect.
      if (status === 401 && isAdminApiRequest(url)) {
        const path =
          typeof window !== "undefined" ? window.location?.pathname || "" : "";
        if (url.includes("/admin/login")) {
          return Promise.reject(error);
        }
        if (typeof window !== "undefined" && isAdminAppRoute(path)) {
          try {
            localStorage.removeItem(ADMIN_TOKEN_KEY);
          } catch {
            /* ignore */
          }
          if (path !== "/admin") {
            window.location.href = "/admin";
          }
        }
        return Promise.reject(error);
      }

      // Try to refresh token once (if we have refresh_token) before forcing logout.
      if (
        status === 401 &&
        originalRequest &&
        !originalRequest.__katalojiRetriedAfterRefresh &&
        !url.includes("/auth/refresh") &&
        !isAdminApiRequest(url)
      ) {
        // Never attempt refresh from public pages.
        const path =
          typeof window !== "undefined" ? window.location?.pathname || "" : "";
        if (isPublicRoute(path)) {
          return Promise.reject(error);
        }

        let refreshToken = null;
        try {
          refreshToken = localStorage.getItem("refresh_token");
        } catch {
          refreshToken = null;
        }

        if (refreshToken) {
          if (isRefreshingToken) {
            // Wait for in-flight refresh, then retry.
            const newToken = await new Promise((resolve) =>
              refreshWaiters.push(resolve)
            );
            if (!newToken) return Promise.reject(error);
            originalRequest.__katalojiRetriedAfterRefresh = true;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          }

          isRefreshingToken = true;
          try {
            const res = await axios.post(
              `${API_URL}/auth/refresh`,
              { refresh_token: refreshToken },
              { headers: { "Content-Type": "application/json" } }
            );

            const newAccess = res?.data?.access_token;
            const newRefresh = res?.data?.refresh_token;
            if (newAccess) {
              try {
                localStorage.setItem("token", newAccess);
                if (newRefresh) localStorage.setItem("refresh_token", newRefresh);
              } catch {
                // ignore
              }
              notifyRefreshWaiters(newAccess);
              originalRequest.__katalojiRetriedAfterRefresh = true;
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newAccess}`;
              return axios(originalRequest);
            }

            notifyRefreshWaiters(null);
          } catch (refreshError) {
            notifyRefreshWaiters(null);
            try {
              localStorage.removeItem("token");
              localStorage.removeItem("refresh_token");
              localStorage.removeItem("user");
            } catch {
              // ignore
            }
            if (typeof window !== "undefined") {
              const p = window.location?.pathname || "";
              if (p !== "/login") {
                window.location.href = "/login";
              }
            }
            return Promise.reject(refreshError);
          } finally {
            isRefreshingToken = false;
          }
        }
      }

      if (status === 401 && !isHandlingUnauthorized) {
        if (typeof window !== "undefined") {
          const path = window.location?.pathname || "";
          // Never force auth checks on public pages like the catalog.
          if (isPublicRoute(path)) {
            return Promise.reject(error);
          }
          if (isAdminAppRoute(path)) {
            try {
              localStorage.removeItem(ADMIN_TOKEN_KEY);
            } catch {
              /* ignore */
            }
            if (path !== "/admin") {
              window.location.href = "/admin";
            }
            return Promise.reject(error);
          }

          isHandlingUnauthorized = true;
          try {
            localStorage.removeItem("token");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("user");
          } catch {
            // ignore
          }

          // Avoid redirect loops if we're already on /login
          if (path !== "/login") {
            window.location.href = "/login";
          }

          setTimeout(() => {
            isHandlingUnauthorized = false;
          }, 1000);
        }
      }

      return Promise.reject(error);
    }
  );
}

